import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CheckIcon, FileIcon, FileTextIcon, ImageIcon, MusicIcon, VideoIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { GenericCard } from "@/components/resources/generic-card";
import { ImageCard } from "@/components/resources/image-card";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Empty, EmptyDescription, EmptyMedia } from "@/components/ui/empty";
import { cn } from "@/components/ui/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { filesInfiniteQueryOptions } from "@/hooks/query/file";
import type { FileItem, FileTypeFilter } from "@/lib/file-utils";
import { filterToMimeTypes } from "@/lib/file-utils";
import * as m from "@/paraglide/messages";

const FILE_TYPE_FILTERS: {
	key: FileTypeFilter;
	label: () => string;
	icon: React.ElementType;
}[] = [
	{ key: "all", label: () => m.sidebar_allFiles(), icon: FileIcon },
	{ key: "image", label: () => m.sidebar_images(), icon: ImageIcon },
	{ key: "audio", label: () => m.sidebar_audio(), icon: MusicIcon },
	{ key: "video", label: () => m.sidebar_video(), icon: VideoIcon },
	{ key: "document", label: () => "PDF", icon: FileTextIcon },
];

type ResourcePickerDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (files: FileItem[]) => void;
};

function ResourcePickerDialog({ open, onOpenChange, onConfirm }: ResourcePickerDialogProps) {
	const [filter, setFilter] = useState<FileTypeFilter>("all");
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery(
		filesInfiniteQueryOptions({
			pageSize: 16,
			fileType: filterToMimeTypes[filter],
			enabled: open,
		}),
	);

	const files = data?.pages.flatMap((page) => page.data) ?? [];
	const filesRef = useRef(files);
	filesRef.current = files;

	const toggleSelection = useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const handleConfirm = useCallback(() => {
		const selected = filesRef.current.filter((f) => selectedIds.has(f.id));
		setSelectedIds(new Set());
		onConfirm(selected);
	}, [onConfirm, selectedIds]);

	useEffect(() => {
		if (!open) {
			setSelectedIds(new Set());
		}
	}, [open]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[80vh] flex-col sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{m.sidebar_resources()}</DialogTitle>
					<DialogDescription className="sr-only">
						{m.sidebar_resources()}
					</DialogDescription>
				</DialogHeader>

				{/* Filter tabs */}
				<div className="flex flex-wrap gap-2">
					{FILE_TYPE_FILTERS.map((f) => (
						<button
							key={f.key}
							type="button"
							onClick={() => setFilter(f.key)}
							className={cn(
								"inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
								filter === f.key
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:bg-muted/80",
							)}
						>
							<f.icon className="size-3.5" />
							{f.label()}
						</button>
					))}
				</div>

				{/* Scrollable file grid */}
				<div className="min-h-0 flex-1 overflow-y-auto">
					{isPending ? (
						<div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
							{Array.from({ length: 8 }).map((_, i) => (
								<Skeleton
									key={`skeleton-${i.toString()}`}
									className="aspect-square rounded-lg"
								/>
							))}
						</div>
					) : files.length === 0 ? (
						<Empty className="py-12">
							<EmptyMedia variant="icon">
								<FileTextIcon className="size-8" />
							</EmptyMedia>
							<EmptyDescription>{m.sidebar_noFiles()}</EmptyDescription>
						</Empty>
					) : (
						<>
							<div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
								{files.map((file) => (
									<SelectableCard
										key={file.id}
										file={file}
										selected={selectedIds.has(file.id)}
										onToggle={toggleSelection}
									/>
								))}
							</div>
							{hasNextPage && (
								<div className="flex justify-center pt-4">
									<Button
										variant="outline"
										size="sm"
										onClick={() => fetchNextPage()}
										disabled={isFetchingNextPage}
									>
										{isFetchingNextPage
											? m.common_loading()
											: m.common_loadMore()}
									</Button>
								</div>
							)}
						</>
					)}
				</div>

				{/* Footer */}
				<DialogFooter className="flex-row items-center sm:justify-between">
					<span className="text-sm text-muted-foreground">
						{selectedIds.size > 0
							? `${selectedIds.size.toString()} selected`
							: "\u00A0"}
					</span>
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => onOpenChange(false)}>
							{m.common_cancel()}
						</Button>
						<Button disabled={selectedIds.size === 0} onClick={handleConfirm}>
							{m.common_confirm()}
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/**
 * Wraps a shared card component with selection UI (checkbox indicator + ring).
 * Reuses ImageCard / GenericCard from @/components/resources.
 */
function SelectableCard({
	file,
	selected,
	onToggle,
}: {
	file: FileItem;
	selected: boolean;
	onToggle: (id: string) => void;
}) {
	const isImage = file.fileType.startsWith("image/");

	return (
		<ButtonPrimitive
			render={<div />}
			nativeButton={false}
			onClick={() => onToggle(file.id)}
			className={cn(
				"group relative cursor-pointer rounded-lg transition-all [&>div]:rounded-lg [&>div]:border-0 [&>div]:shadow-none [&>div]:hover\\:shadow-none",
				selected ? "ring-primary ring-2" : "ring-1 ring-border hover:ring-foreground/20",
			)}
		>
			{/* Selection indicator */}
			<div
				className={cn(
					"absolute top-1.5 right-1.5 z-10 flex size-5 items-center justify-center rounded-full border transition-colors",
					selected
						? "border-primary bg-primary text-primary-foreground"
						: "border-muted-foreground/40 bg-background/80 opacity-0 group-hover:opacity-100",
				)}
			>
				{selected && <CheckIcon className="size-3" />}
			</div>

			{/* Reuse shared card — pointer-events-none so click goes to wrapper */}
			<div className="pointer-events-none">
				{isImage ? <ImageCard file={file} /> : <GenericCard file={file} />}
			</div>
		</ButtonPrimitive>
	);
}

export { ResourcePickerDialog };
